import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

import { GroupEntity } from '@backstage/catalog-model';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import BusinessIcon from '@material-ui/icons/Business';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import GroupIcon from '@material-ui/icons/Group';
import StarIcon from '@material-ui/icons/Star';

import { TeamDescendantsTree } from './hooks/useTeamTreeFromAPI';

const useStyles = makeStyles(theme => ({
  accordion: {
    boxShadow: 'none',
    border: 'none',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
    backgroundColor: 'transparent',
  },
  accordionSummary: {
    minHeight: 56,
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-expanded': {
      minHeight: 56,
      backgroundColor: theme.palette.action.selected,
    },
    '& .MuiAccordionSummary-content': {
      margin: theme.spacing(1.5, 0),
      alignItems: 'center',
      '&.Mui-expanded': {
        margin: theme.spacing(1.5, 0),
      },
    },
  },
  accordionDetails: {
    padding: 0,
    display: 'block',
    paddingLeft: theme.spacing(3),
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(3),
  },
  expandIcon: {
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease',
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)',
  },
  teamContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  teamIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  teamIconManaged: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  teamContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flexGrow: 1,
    minWidth: 0,
  },
  teamNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  teamName: {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: 14,
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  teamMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  teamOrgId: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  teamDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  managedChip: {
    height: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: 4,
    },
  },
  childrenBadge: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    padding: '2px 8px',
    borderRadius: 12,
  },
  leafRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    minHeight: 56,
    borderRadius: 6,
    transition: 'all 0.2s ease',
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  ouBuBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '2px 6px',
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 500,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
    color:
      theme.palette.type === 'dark'
        ? theme.palette.text.secondary
        : 'rgba(0, 0, 0, 0.6)',
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.12)'
    }`,
    '& svg': {
      fontSize: 11,
      opacity: 0.7,
    },
  },
  loadingText: {
    fontSize: 12,
    color: theme.palette.text.disabled,
  },
}));

interface TeamTreeNodeProps {
  node: TeamDescendantsTree;
  level: number;
  onTeamClick?: (teamName: string) => void;
}

export const TeamTreeNode: React.FC<TeamTreeNodeProps> = ({
  node,
  level,
  onTeamClick,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [expanded, setExpanded] = useState(level === 0); // Root level expanded by default

  // Fetch user's email
  const { value: userEmail } = useAsync(async () => {
    try {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const userEntity = await catalogApi.getEntityByRef(userEntityRef);
      const profile = userEntity?.spec?.profile as
        | { email?: string }
        | undefined;
      return profile?.email || null;
    } catch {
      return null;
    }
  }, [identityApi, catalogApi]);

  // Fetch team entity details from catalog
  const { value: teamEntity, loading: entityLoading } = useAsync(async () => {
    try {
      const entity = (await catalogApi.getEntityByRef(
        `group:default/${node.team_id}`,
      )) as GroupEntity;
      return entity;
    } catch {
      return null;
    }
  }, [node.team_id, catalogApi]);

  const hasChildren = node.children && node.children.length > 0;
  const childrenCount = node.children?.length || 0;

  // Check if current user is a manager of this team
  const isUserManager = React.useMemo(() => {
    if (!userEmail || !teamEntity?.metadata?.managers) {
      return false;
    }

    const managers = teamEntity.metadata.managers as Array<{
      email: string;
      name?: string;
    }>;

    return managers.some(
      manager => manager.email?.toLowerCase() === userEmail.toLowerCase(),
    );
  }, [userEmail, teamEntity]);

  const handleTeamNameClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion toggle
    if (onTeamClick) {
      onTeamClick(node.team_id);
    } else {
      navigate(`/catalog/default/Group/${node.team_id}`);
    }
  };

  // Render team info content (shared between accordion and leaf)
  const renderTeamContent = () => {
    if (entityLoading) {
      return (
        <Box className={classes.teamContainer}>
          <Box className={classes.teamIcon}>
            <CircularProgress size={20} />
          </Box>
          <Box className={classes.teamContent}>
            <Typography className={classes.loadingText}>
              Loading team details...
            </Typography>
          </Box>
        </Box>
      );
    }

    const title =
      teamEntity?.metadata?.title || teamEntity?.metadata?.name || node.team_id;

    return (
      <Box className={classes.teamContainer}>
        <Box
          className={`${classes.teamIcon} ${
            isUserManager ? classes.teamIconManaged : ''
          }`}
        >
          {(() => {
            if (isUserManager) {
              return <StarIcon style={{ fontSize: 20 }} />;
            }
            if (hasChildren) {
              return expanded ? (
                <FolderOpenIcon style={{ fontSize: 20 }} />
              ) : (
                <FolderIcon style={{ fontSize: 20 }} />
              );
            }
            return <GroupIcon style={{ fontSize: 20 }} />;
          })()}
        </Box>

        <Box className={classes.teamContent}>
          <Box className={classes.teamNameRow}>
            <Typography
              className={classes.teamName}
              onClick={handleTeamNameClick}
            >
              {title}
            </Typography>
            {isUserManager && (
              <Chip
                icon={<StarIcon />}
                label="Manager"
                size="small"
                className={classes.managedChip}
              />
            )}
            {hasChildren && (
              <Typography className={classes.childrenBadge}>
                {childrenCount} {childrenCount === 1 ? 'team' : 'teams'}
              </Typography>
            )}
          </Box>
          <Box className={classes.teamMeta}>
            <Typography className={classes.teamOrgId}>
              {node.team_id?.toUpperCase() || 'UNKNOWN'}
            </Typography>
            {(node.team_ou || node.team_bu) && (
              <Box style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                {node.team_ou && (
                  <Tooltip title="Organizational Unit" arrow placement="top">
                    <Box className={classes.ouBuBadge}>
                      <AccountTreeIcon />
                      <span>{node.team_ou}</span>
                    </Box>
                  </Tooltip>
                )}
                {node.team_bu && (
                  <Tooltip title="Business Unit" arrow placement="top">
                    <Box className={classes.ouBuBadge}>
                      <BusinessIcon />
                      <span>{node.team_bu}</span>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // If no children, render as simple row
  if (!hasChildren) {
    return <Box className={classes.leafRow}>{renderTeamContent()}</Box>;
  }

  // Render as accordion for expandable teams
  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={
          <ChevronRightIcon
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIconExpanded : ''
            }`}
          />
        }
      >
        {renderTeamContent()}
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {node.children?.map(child => (
          <TeamTreeNode
            key={child.team_id}
            node={child}
            level={level + 1}
            onTeamClick={onTeamClick}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
